import { Controller } from "@hotwired/stimulus";
import { Drawer } from "flowbite";

const COLLAPSIBLE_IDS = [
  "dropdownMembersHub",
  "dropdownTransferHub",
  "dropdownFormationHub",
  "dropdownFaq",
  "dropdownSystemSettings",
];

const DEFAULT_OPEN_IDS = ["dropdownMembersHub"];

const SIDEBAR_NAV_TIERS = {
  primary: {
    base: "flex items-center gap-3 rounded-xl px-3 py-2.5 transition",
    active:
      "text-white bg-white/15 font-semibold ring-1 ring-white/20 shadow-sm",
    inactive: "text-white/90 hover:bg-white/10",
  },
  nested: {
    base: "flex items-center gap-3 py-2 transition rounded-xl pe-3 ps-10",
    active:
      "text-white bg-white/15 font-semibold ring-1 ring-white/20 shadow-sm",
    inactive: "text-white/90 hover:bg-white/10",
  },
};

function normalizePath(path) {
  const p = path.replace(/\/$/, "");
  return p === "" ? "/" : p;
}

function sidebarNavActive(linkPath, currentPath, rootPath) {
  const base = normalizePath(
    new URL(linkPath, window.location.origin).pathname
  );
  const cur = normalizePath(currentPath);
  const root = normalizePath(rootPath || "/");
  if (base === root) {
    return cur === root;
  }
  return cur === base || currentPath.startsWith(`${base}/`);
}

// Connects to data-controller="drawer"
export default class extends Controller {
  static targets = ["drawer"];

  connect() {
    const options = {
      placement: "left",
      backdrop: false,
      bodyScrolling: false,
      edge: false,
      edgeOffset: "",
    };

    const instanceOptions = {
      id: "drawer",
      override: true,
    };

    this.drawer = new Drawer(drawer, options, instanceOptions);

    let counter = localStorage.getItem("drawerCounter");
    counter = counter === null ? 0 : parseInt(counter, 10);
    this.counter = counter;

    COLLAPSIBLE_IDS.forEach((id) => this.restoreCollapsibleState(id));

    this.boundHighlightSidebarNav = this.highlightSidebarNav.bind(this);
    this.boundOnMainFrameLoad = (event) => {
      if (event.target.id === "main_content_frame") this.highlightSidebarNav();
    };
    this.highlightSidebarNav();
    document.addEventListener("turbo:frame-load", this.boundOnMainFrameLoad);
    document.addEventListener("turbo:load", this.boundHighlightSidebarNav);

    if (this.counter % 2 === 1) {
      this.show();
    } else {
      this.drawer.hide();
      this.dismissBackdropOnly();
    }

    const backdrop = document.getElementById("drawerBackdrop");
    backdrop.addEventListener("click", (event) => {
      if (event.target === backdrop) {
        this.hide();
      }
    });
  }

  disconnect() {
    document.removeEventListener("turbo:frame-load", this.boundOnMainFrameLoad);
    document.removeEventListener("turbo:load", this.boundHighlightSidebarNav);
  }

  highlightSidebarNav() {
    const drawer = this.drawerTarget;
    if (!drawer) return;

    const rootPath = document.body?.dataset?.rootPath || "/";
    const currentPath = window.location.pathname;
    let activeInCollapsible = null;

    drawer.querySelectorAll("a[data-sidebar-nav]").forEach((a) => {
      const tier = a.dataset.navTier || "nested";
      const spec = SIDEBAR_NAV_TIERS[tier] || SIDEBAR_NAV_TIERS.nested;
      const href = a.getAttribute("href");
      if (!href) return;

      const active = sidebarNavActive(href, currentPath, rootPath);
      a.className = `${spec.base} ${active ? spec.active : spec.inactive}`;

      if (active) {
        const panel = a.closest("[id]");
        if (panel && COLLAPSIBLE_IDS.includes(panel.id)) {
          activeInCollapsible = panel.id;
        }
      }
    });

    if (activeInCollapsible) {
      this.ensureCollapsibleOpen(activeInCollapsible);
    }
  }

  ensureCollapsibleOpen(collapsibleId) {
    const collapsible = document.getElementById(collapsibleId);
    if (!collapsible || collapsible.classList.contains("is-open")) return;

    const target = document.querySelector(`[aria-controls="${collapsibleId}"]`);
    const chevron = target ? this.chevronFor(target) : null;

    collapsible.classList.add("is-open");
    localStorage.setItem(`${collapsibleId}_state`, "open");
    if (chevron) {
      chevron.style.transform = "rotate(180deg)";
      localStorage.setItem(`${collapsibleId}_svg_rotation`, "180");
    }
  }

  dismissBackdropOnly() {
    const backdrop = document.getElementById("drawerBackdrop");
    backdrop.classList.remove(
      "flex",
      "bg-gray-800/70",
      "overflow-y-auto",
      "overflow-x-hidden",
      "fixed",
      "inset-0",
      "z-50",
      "justify-center",
      "items-center",
      "w-full",
      "h-full",
      "transition-opacity",
      "duration-300"
    );
  }

  show() {
    this.drawer.show();
    this.counter = 1;
    localStorage.setItem("drawerCounter", this.counter);

    const backdrop = document.getElementById("drawerBackdrop");
    backdrop.classList.add(
      "flex",
      "bg-gray-800/70",
      "overflow-y-auto",
      "overflow-x-hidden",
      "fixed",
      "inset-0",
      "z-50",
      "justify-center",
      "items-center",
      "w-full",
      "h-full",
      "transition-opacity",
      "duration-300",
      "opacity-100"
    );
    backdrop.classList.remove("opacity-0");
  }

  hide() {
    this.drawer.hide();
    if (this.counter > 0) {
      this.counter = 0;
    }
    localStorage.setItem("drawerCounter", this.counter);

    COLLAPSIBLE_IDS.forEach((id) => this.collapseSection(id));
    this.resetAllSvgRotations();

    const backdrop = document.getElementById("drawerBackdrop");
    this.dismissBackdropOnly();
  }

  toggle() {
    let counter = localStorage.getItem("drawerCounter");
    counter = counter === null ? 0 : parseInt(counter, 10);

    counter++;
    localStorage.setItem("drawerCounter", counter);

    this.counter = counter;

    if (this.counter % 2 === 1) {
      this.show();
    } else {
      this.hide();
    }
  }

  chevronFor(target) {
    const svgs = target.querySelectorAll("svg");
    return svgs[svgs.length - 1];
  }

  toggleCollapse(event) {
    const target = event.currentTarget;
    const collapsibleId = target.getAttribute("aria-controls");
    const collapsible = document.getElementById(collapsibleId);
    const chevron = this.chevronFor(target);

    if (!collapsible.classList.contains("is-open")) {
      if (chevron) {
        chevron.style.transform = "rotate(180deg)";
        localStorage.setItem(`${collapsibleId}_svg_rotation`, "180");
      }
      collapsible.classList.add("is-open");
      localStorage.setItem(`${collapsibleId}_state`, "open");
    } else {
      if (chevron) {
        chevron.style.transform = "rotate(0deg)";
        localStorage.setItem(`${collapsibleId}_svg_rotation`, "0");
      }
      collapsible.classList.remove("is-open");
      localStorage.setItem(`${collapsibleId}_state`, "closed");
    }
  }

  restoreCollapsibleState(collapsibleId) {
    const collapsible = document.getElementById(collapsibleId);
    if (!collapsible) return;

    const state = localStorage.getItem(`${collapsibleId}_state`);
    let isOpen;
    if (state === "open") isOpen = true;
    else if (state === "closed") isOpen = false;
    else isOpen = DEFAULT_OPEN_IDS.includes(collapsibleId);

    if (isOpen) {
      collapsible.classList.add("is-open");
    } else {
      collapsible.classList.remove("is-open");
    }

    let rotation = localStorage.getItem(`${collapsibleId}_svg_rotation`);
    if (rotation === null) {
      rotation = isOpen ? "180" : "0";
    }

    const target = document.querySelector(`[aria-controls="${collapsibleId}"]`);
    if (target) {
      const chevron = this.chevronFor(target);
      if (chevron) chevron.style.transform = `rotate(${rotation}deg)`;
    }
  }

  collapseSection(collapsibleId) {
    const collapsible = document.getElementById(collapsibleId);
    if (!collapsible) return;
    collapsible.classList.remove("is-open");
    localStorage.setItem(`${collapsibleId}_state`, "closed");
    localStorage.setItem(`${collapsibleId}_svg_rotation`, "0");
  }

  resetAllSvgRotations() {
    document.querySelectorAll(".reset-rotation").forEach((svg) => {
      svg.style.transform = "rotate(0deg)";
    });
  }
}
